import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../components/ui/tooltip";
import {
  BedDouble,
  Users,
  CreditCard,
  AlertTriangle,
  TrendingUp,
  CheckCircle2,
  Clock,
  Building2,
  Calendar,
  Bell,
  ChevronRight,
  Wallet,
  History,
  DoorOpen,
  Loader2,
  X,
  User,
} from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { de } from "date-fns/locale";

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [activities, setActivities] = useState([]);
  const [expiringLeistungen, setExpiringLeistungen] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [allActivities, setAllActivities] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, activitiesRes, expiringRes, roomsRes, bookingsRes] = await Promise.all([
        api.get("/dashboard/stats"),
        api.get("/dashboard/recent-activity"),
        api.get("/dashboard/expiring-leistungen?days=60"),
        api.get("/rooms"),
        api.get("/bookings?status=active"),
      ]);
      setStats(statsRes.data);
      setActivities(activitiesRes.data);
      setExpiringLeistungen(expiringRes.data);
      setRooms(roomsRes.data || []);
      setBookings(bookingsRes.data || []);
    } catch (error) {
      console.error("Dashboard error:", error);
      toast.error("Daten konnten nicht geladen werden");
    } finally {
      setLoading(false);
    }
  };

  const fetchAllActivities = async () => {
    setLoadingHistory(true);
    try {
      const res = await api.get("/dashboard/recent-activity?limit=100");
      setAllActivities(res.data);
    } catch (error) {
      console.error("Failed to fetch all activities:", error);
      toast.error("Aktivitäten konnten nicht geladen werden");
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleOpenHistory = () => {
    setHistoryDialogOpen(true);
    fetchAllActivities();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("de-DE", {
      style: "currency",
      currency: "EUR",
    }).format(value || 0);
  };

  const getUrgencyBadge = (urgency, daysRemaining) => {
    if (urgency === "critical") {
      return (
        <Badge className="status-overdue text-xs">
          <AlertTriangle className="h-3 w-3 mr-1" />
          {daysRemaining}T
        </Badge>
      );
    } else if (urgency === "warning") {
      return (
        <Badge className="status-pending text-xs">
          <Clock className="h-3 w-3 mr-1" />
          {daysRemaining}T
        </Badge>
      );
    }
    return (
      <Badge className="status-paid text-xs">
        {daysRemaining}T
      </Badge>
    );
  };

  return (
    <div className="space-y-4 sm:space-y-6 lg:space-y-8" data-testid="dashboard-page">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Dashboard
        </h1>
        <p className="text-sm sm:text-base text-slate-600 mt-1">
          Übersicht Ihrer Pensionsverwaltung
        </p>
      </div>

      {/* Critical Alert - Expiring Leistungen */}
      {expiringLeistungen.filter(l => l.urgency === "critical").length > 0 && (
        <Card className="border-red-200 bg-red-50" data-testid="critical-alert">
          <CardContent className="pt-4 sm:pt-6">
            <div className="flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-4">
              <div className="h-10 w-10 rounded-lg bg-red-100 flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="h-5 w-5 text-red-600" strokeWidth={1.5} />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-red-800 text-sm sm:text-base" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Dringende Aufmerksamkeit erforderlich!
                </h3>
                <p className="text-xs sm:text-sm text-red-700 mt-1">
                  {expiringLeistungen.filter(l => l.urgency === "critical").length} Leistungsübernahme(n) laufen in weniger als 14 Tagen ab.
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="border-red-300 text-red-700 hover:bg-red-100 w-full sm:w-auto"
                onClick={() => document.getElementById('expiring-section')?.scrollIntoView({ behavior: 'smooth' })}
              >
                Details
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        {/* Occupancy Rate */}
        <Card className="border-slate-200 hover:shadow-md transition-shadow" data-testid="stat-occupancy">
          <CardHeader className="flex flex-row items-center justify-between pb-2 p-3 sm:p-6 sm:pb-2">
            <CardTitle className="text-xs sm:text-sm font-medium text-slate-600">Belegung</CardTitle>
            <div className="h-8 w-8 sm:h-10 sm:w-10 rounded-lg bg-blue-50 flex items-center justify-center">
              <TrendingUp className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600" strokeWidth={1.5} />
            </div>
          </CardHeader>
          <CardContent className="p-3 sm:p-6 pt-0 sm:pt-0">
            <div className="text-xl sm:text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {stats?.occupancy_rate || 0}%
            </div>
            <Progress value={stats?.occupancy_rate || 0} className="mt-2 h-1.5 sm:h-2" />
            <p className="text-[10px] sm:text-xs text-slate-500 mt-2">
              {stats?.occupied_beds || 0}/{stats?.total_beds || 0} Betten
            </p>
          </CardContent>
        </Card>

        {/* Available Beds */}
        <Card className="border-slate-200 hover:shadow-md transition-shadow" data-testid="stat-beds">
          <CardHeader className="flex flex-row items-center justify-between pb-2 p-3 sm:p-6 sm:pb-2">
            <CardTitle className="text-xs sm:text-sm font-medium text-slate-600">Verfügbar</CardTitle>
            <div className="h-8 w-8 sm:h-10 sm:w-10 rounded-lg bg-emerald-50 flex items-center justify-center">
              <BedDouble className="h-4 w-4 sm:h-5 sm:w-5 text-emerald-600" strokeWidth={1.5} />
            </div>
          </CardHeader>
          <CardContent className="p-3 sm:p-6 pt-0 sm:pt-0">
            <div className="text-xl sm:text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {stats?.available_beds || 0}
            </div>
            <p className="text-[10px] sm:text-xs text-slate-500 mt-2">
              {stats?.total_rooms || 0} Zimmer
            </p>
          </CardContent>
        </Card>

        {/* Active Guests */}
        <Card className="border-slate-200 hover:shadow-md transition-shadow" data-testid="stat-guests">
          <CardHeader className="flex flex-row items-center justify-between pb-2 p-3 sm:p-6 sm:pb-2">
            <CardTitle className="text-xs sm:text-sm font-medium text-slate-600">Bewohner</CardTitle>
            <div className="h-8 w-8 sm:h-10 sm:w-10 rounded-lg bg-violet-50 flex items-center justify-center">
              <Users className="h-4 w-4 sm:h-5 sm:w-5 text-violet-600" strokeWidth={1.5} />
            </div>
          </CardHeader>
          <CardContent className="p-3 sm:p-6 pt-0 sm:pt-0">
            <div className="text-xl sm:text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {stats?.active_guests || 0}
            </div>
            <p className="text-[10px] sm:text-xs text-slate-500 mt-2">
              {stats?.total_guests || 0} registriert
            </p>
          </CardContent>
        </Card>

        {/* Expiring Leistungen */}
        <Card 
          className={`border-slate-200 hover:shadow-md transition-shadow cursor-pointer ${
            (stats?.expiring_leistungen_30_days || 0) > 0 ? 'border-amber-200 bg-amber-50/30' : ''
          }`}
          onClick={() => document.getElementById('expiring-section')?.scrollIntoView({ behavior: 'smooth' })}
          data-testid="stat-leistungen"
        >
          <CardHeader className="flex flex-row items-center justify-between pb-2 p-3 sm:p-6 sm:pb-2">
            <CardTitle className="text-xs sm:text-sm font-medium text-slate-600">Ablaufend</CardTitle>
            <div className={`h-8 w-8 sm:h-10 sm:w-10 rounded-lg flex items-center justify-center ${
              (stats?.expiring_leistungen_30_days || 0) > 0 ? 'bg-amber-100' : 'bg-slate-100'
            }`}>
              <Calendar className={`h-4 w-4 sm:h-5 sm:w-5 ${
                (stats?.expiring_leistungen_30_days || 0) > 0 ? 'text-amber-600' : 'text-slate-400'
              }`} strokeWidth={1.5} />
            </div>
          </CardHeader>
          <CardContent className="p-3 sm:p-6 pt-0 sm:pt-0">
            <div className={`text-xl sm:text-2xl font-bold ${
              (stats?.expiring_leistungen_30_days || 0) > 0 ? 'text-amber-600' : 'text-slate-900'
            }`} style={{ fontFamily: 'Manrope, sans-serif' }}>
              {stats?.expiring_leistungen_30_days || 0}
            </div>
            <p className="text-[10px] sm:text-xs text-slate-500 mt-2">
              In 30 Tagen
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Second Row - 3 Column Layout */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
        {/* Expiring Leistungsübernahmen */}
        <Card className="border-slate-200" id="expiring-section" data-testid="expiring-leistungen-card">
          <CardHeader className="flex flex-row items-center justify-between p-4 pb-2">
            <div className="flex items-center gap-2">
              <Bell className="h-4 w-4 text-amber-600" />
              <CardTitle className="text-sm font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Ablaufende Leistungen
              </CardTitle>
            </div>
            <Button variant="ghost" size="sm" onClick={() => navigate('/guests')} className="hidden sm:flex h-7 px-2 text-xs">
              Alle
              <ChevronRight className="h-3 w-3 ml-1" />
            </Button>
          </CardHeader>
          <CardContent className="p-4 pt-2">
            {expiringLeistungen.length > 0 ? (
              <div className="space-y-2">
                {expiringLeistungen.slice(0, 4).map((item, index) => (
                  <div 
                    key={index} 
                    className={`flex items-center justify-between p-2 rounded-lg border ${
                      item.urgency === 'critical' ? 'bg-red-50 border-red-200' : 'bg-slate-50 border-slate-200'
                    }`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-xs truncate">{item.guest_name}</div>
                      <div className="text-[10px] text-slate-500">
                        {item.room_number ? `Zi. ${item.room_number}` : ''} • {format(parseISO(item.leistung_end_date), "dd.MM", { locale: de })}
                      </div>
                    </div>
                    {getUrgencyBadge(item.urgency, item.days_remaining)}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6 text-slate-500">
                <CheckCircle2 className="h-8 w-8 mx-auto text-emerald-300 mb-2" />
                <p className="text-xs">Keine ablaufenden Leistungen</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Payment Status */}
        <Card className="border-slate-200" data-testid="payment-status-card">
          <CardHeader className="p-4 pb-2">
            <CardTitle className="text-sm font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>Zahlungen</CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-2 space-y-2">
            <div className="flex items-center justify-between p-2 bg-amber-50 rounded-lg">
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-amber-600" strokeWidth={1.5} />
                <span className="text-xs font-medium text-amber-800">Ausstehend</span>
              </div>
              <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-200 text-xs h-5">
                {stats?.pending_payments || 0}
              </Badge>
            </div>
            <div className="flex items-center justify-between p-2 bg-red-50 rounded-lg">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-red-600" strokeWidth={1.5} />
                <span className="text-xs font-medium text-red-800">Überfällig</span>
              </div>
              <Badge variant="outline" className="bg-red-100 text-red-800 border-red-200 text-xs h-5">
                {stats?.overdue_payments || 0}
              </Badge>
            </div>
            <div className="pt-2 border-t border-slate-100">
              <div className="flex items-center justify-between">
                <span className="text-xs text-slate-600">Umsatz (Monat)</span>
                <span className="font-mono font-bold text-sm" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                  {formatCurrency(stats?.total_revenue_this_month)}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Open Balances & Room Overview Combined */}
        <div className="space-y-4">
          {/* Open Balances - Offene Salden */}
          <Card 
            className={`border-slate-200 cursor-pointer hover:shadow-md transition-shadow ${
              (stats?.guests_with_negative_balance || 0) > 0 ? 'border-red-200 bg-red-50/30' : ''
            }`}
            onClick={() => navigate('/accounts')}
            data-testid="open-balance-card"
          >
            <CardHeader className="p-4 pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Offene Salden
                </CardTitle>
                <Wallet className={`h-4 w-4 ${(stats?.guests_with_negative_balance || 0) > 0 ? 'text-red-500' : 'text-slate-400'}`} />
              </div>
            </CardHeader>
            <CardContent className="p-4 pt-2 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs text-slate-600">Bewohner mit Schulden</span>
                <Badge variant="outline" className={`text-xs h-5 ${
                  (stats?.guests_with_negative_balance || 0) > 0 
                    ? 'bg-red-100 text-red-800 border-red-200' 
                    : 'bg-slate-100 text-slate-600 border-slate-200'
                }`}>
                  {stats?.guests_with_negative_balance || 0}
                </Badge>
              </div>
              <div className="flex items-center justify-between pt-2 border-t border-slate-100">
                <span className="text-xs text-slate-600">Gesamtschulden</span>
                <span className={`font-mono font-bold text-sm ${
                  (stats?.total_open_balance || 0) < 0 ? 'text-red-600' : 'text-slate-900'
                }`} style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                  {formatCurrency(Math.abs(stats?.total_open_balance || 0))}
                </span>
              </div>
            </CardContent>
          </Card>

          {/* Quick Stats - Zimmerübersicht */}
          <Card className="border-slate-200" data-testid="quick-stats-card">
            <CardHeader className="p-4 pb-2">
              <CardTitle className="text-sm font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>Zimmerübersicht</CardTitle>
            </CardHeader>
            <CardContent className="p-4 pt-2 space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="h-2 w-2 rounded-full bg-emerald-500"></div>
                  <span className="text-xs text-slate-600">Verfügbar</span>
                </div>
                <span className="text-xs font-medium text-slate-900">{stats?.available_beds || 0} Betten</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="h-2 w-2 rounded-full bg-slate-400"></div>
                  <span className="text-xs text-slate-600">Belegt</span>
                </div>
                <span className="text-xs font-medium text-slate-900">{stats?.occupied_beds || 0} Betten</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Building2 className="h-3 w-3 text-slate-400" strokeWidth={1.5} />
                  <span className="text-xs text-slate-600">Zimmer</span>
                </div>
                <span className="text-xs font-medium text-slate-900">{stats?.total_rooms || 0}</span>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Room Mini Cards - Grouped by Floor */}
      {rooms.length > 0 && (
        <Card className="border-slate-200" data-testid="room-mini-cards">
          <CardHeader className="p-4 pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-semibold flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                <DoorOpen className="h-4 w-4 text-slate-500" />
                Zimmerübersicht
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={() => navigate('/rooms')} className="h-7 px-2 text-xs">
                Alle anzeigen
                <ChevronRight className="h-3 w-3 ml-1" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-4 pt-2">
            <TooltipProvider delayDuration={200}>
              {/* Group rooms by floor */}
              {(() => {
                // Group rooms by floor
                const roomsByFloor = rooms.reduce((acc, room) => {
                  const floor = room.floor || 0;
                  if (!acc[floor]) acc[floor] = [];
                  acc[floor].push(room);
                  return acc;
                }, {});
                
                // Sort floors and render
                const sortedFloors = Object.keys(roomsByFloor).map(Number).sort((a, b) => a - b);
                
                // Floor name helper
                const getFloorName = (floor) => {
                  if (floor === 0) return 'EG';
                  return `${floor}. OG`;
                };
                
                return (
                  <div className="space-y-3">
                    {sortedFloors.map((floor) => (
                      <div key={floor}>
                        <div className="text-xs font-semibold text-slate-500 mb-1.5 flex items-center gap-2">
                          <Building2 className="h-3 w-3" />
                          {getFloorName(floor)}
                        </div>
                        <div className="grid grid-cols-5 sm:grid-cols-8 md:grid-cols-10 lg:grid-cols-12 xl:grid-cols-14 gap-1.5">
                          {roomsByFloor[floor]
                            .sort((a, b) => a.room_number.localeCompare(b.room_number, undefined, { numeric: true }))
                            .map((room) => {
                              // Find active bookings for this room
                              const roomBookings = bookings.filter(b => b.room_id === room.id && b.status === 'active');
                              const occupiedBeds = roomBookings.length;
                              const totalBeds = room.beds?.length || 0;
                              
                              // Determine room status: empty, partial, full
                              const isEmpty = occupiedBeds === 0;
                              const isFull = occupiedBeds >= totalBeds && totalBeds > 0;
                              const isPartial = occupiedBeds > 0 && occupiedBeds < totalBeds;
                              
                              // Card colors based on status
                              const cardColors = isEmpty 
                                ? 'bg-emerald-50 border-emerald-200 hover:bg-emerald-100' 
                                : isPartial 
                                  ? 'bg-amber-50 border-amber-200 hover:bg-amber-100'
                                  : 'bg-slate-100 border-slate-300 hover:bg-slate-200';
                              
                              const textColor = isEmpty 
                                ? 'text-emerald-700' 
                                : isPartial 
                                  ? 'text-amber-700'
                                  : 'text-slate-600';
                              
                              // Tooltip colors matching card
                              const tooltipBgColor = isEmpty 
                                ? 'bg-emerald-50 border-emerald-200' 
                                : isPartial 
                                  ? 'bg-amber-50 border-amber-200'
                                  : 'bg-slate-100 border-slate-300';
                              
                              return (
                                <Tooltip key={room.id}>
                                  <TooltipTrigger asChild>
                                    <div
                                      onClick={() => navigate('/rooms')}
                                      className={`p-1.5 rounded-lg border cursor-pointer transition-all hover:shadow-md hover:scale-105 ${cardColors}`}
                                    >
                                      <div className="text-center">
                                        <div className={`text-[11px] font-bold ${textColor}`}>
                                          {room.room_number}
                                        </div>
                                        <div className="text-[9px] text-slate-500">
                                          {occupiedBeds}/{totalBeds}
                                        </div>
                                      </div>
                                    </div>
                                  </TooltipTrigger>
                                  <TooltipContent side="top" className={`max-w-xs border ${tooltipBgColor}`}>
                                    <div className="space-y-1">
                                      <div className={`font-semibold text-sm border-b pb-1 ${
                                        isEmpty ? 'border-emerald-200 text-emerald-800' 
                                        : isPartial ? 'border-amber-200 text-amber-800'
                                        : 'border-slate-300 text-slate-700'
                                      }`}>
                                        Zimmer {room.room_number} ({getFloorName(floor)})
                                      </div>
                                      {(isFull || isPartial) ? (
                                        <div className="space-y-1.5">
                                          {roomBookings.map((booking, idx) => (
                                            <div key={idx} className="flex items-center gap-2 text-xs">
                                              <User className={`h-3 w-3 ${isPartial ? 'text-amber-500' : 'text-slate-400'}`} />
                                              <span className={isPartial ? 'text-amber-800' : 'text-slate-700'}>
                                                {booking.guest_name || 'Unbekannt'}
                                              </span>
                                              {booking.bed_number && (
                                                <Badge variant="outline" className={`text-[10px] h-4 px-1 ${
                                                  isPartial ? 'border-amber-300 text-amber-700' : 'border-slate-300 text-slate-600'
                                                }`}>
                                                  {booking.bed_number}
                                                </Badge>
                                              )}
                                            </div>
                                          ))}
                                          {isPartial && (
                                            <div className="text-[10px] text-amber-600 pt-1 border-t border-amber-200">
                                              {totalBeds - occupiedBeds} {totalBeds - occupiedBeds === 1 ? 'Bett' : 'Betten'} frei
                                            </div>
                                          )}
                                        </div>
                                      ) : (
                                        <div className="text-xs text-emerald-600 flex items-center gap-1">
                                          <CheckCircle2 className="h-3 w-3" />
                                          Verfügbar ({totalBeds} {totalBeds === 1 ? 'Bett' : 'Betten'})
                                        </div>
                                      )}
                                    </div>
                                  </TooltipContent>
                                </Tooltip>
                              );
                            })}
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </TooltipProvider>
            <div className="flex items-center gap-4 mt-3 pt-3 border-t border-slate-100">
              <div className="flex items-center gap-2 text-xs text-slate-500">
                <div className="h-3 w-3 rounded bg-emerald-50 border border-emerald-200"></div>
                <span>Verfügbar</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-slate-500">
                <div className="h-3 w-3 rounded bg-amber-50 border border-amber-200"></div>
                <span>Teilweise</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-slate-500">
                <div className="h-3 w-3 rounded bg-slate-100 border border-slate-300"></div>
                <span>Belegt</span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent Activity */}
      <Card className="border-slate-200" data-testid="recent-activity-card">
        <CardHeader className="p-4 pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-semibold flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <History className="h-4 w-4 text-slate-500" />
              Letzte Aktivitäten
            </CardTitle>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={handleOpenHistory}
              className="h-7 px-2 text-xs"
            >
              Alle anzeigen
              <ChevronRight className="h-3 w-3 ml-1" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-4 pt-2">
          <div className="space-y-2">
            {activities.length > 0 ? (
              activities.slice(0, 5).map((activity, index) => {
                const getActionColor = (action) => {
                  switch(action) {
                    case 'create': return 'bg-emerald-500';
                    case 'update': return 'bg-blue-500';
                    case 'delete': return 'bg-red-500';
                    default: return activity.type === 'payment' ? 'bg-emerald-500' : 'bg-blue-500';
                  }
                };
                
                const formatTimestamp = (ts) => {
                  if (!ts) return '';
                  try {
                    const date = new Date(ts);
                    return date.toLocaleDateString('de-DE', {
                      day: '2-digit',
                      month: '2-digit',
                      hour: '2-digit',
                      minute: '2-digit'
                    });
                  } catch {
                    return '';
                  }
                };
                
                return (
                  <div key={index} className="flex items-start gap-2 text-xs border-b border-slate-100 pb-2 last:border-0">
                    <div className={`mt-1.5 h-2 w-2 rounded-full flex-shrink-0 ${getActionColor(activity.action)}`}></div>
                    <div className="flex-1 min-w-0">
                      <p className="text-slate-700 leading-snug truncate">{activity.message}</p>
                      <p className="text-[10px] text-slate-400 mt-0.5">
                        {formatTimestamp(activity.timestamp)}
                      </p>
                    </div>
                  </div>
                );
              })
            ) : (
              <p className="text-xs text-slate-500 text-center py-4">
                Keine Aktivitäten vorhanden
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Activity History Dialog */}
      <Dialog open={historyDialogOpen} onOpenChange={setHistoryDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5" />
              Aktivitätenhistorie
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto pr-2">
            {loadingHistory ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
              </div>
            ) : allActivities.length > 0 ? (
              <div className="space-y-2">
                {allActivities.map((activity, index) => {
                  const getActionColor = (action) => {
                    switch(action) {
                      case 'create': return 'bg-emerald-500';
                      case 'update': return 'bg-blue-500';
                      case 'delete': return 'bg-red-500';
                      default: return activity.type === 'payment' ? 'bg-emerald-500' : 'bg-blue-500';
                    }
                  };
                  
                  const getActionLabel = (action) => {
                    switch(action) {
                      case 'create': return 'Erstellt';
                      case 'update': return 'Aktualisiert';
                      case 'delete': return 'Gelöscht';
                      default: return '';
                    }
                  };
                  
                  const formatTimestamp = (ts) => {
                    if (!ts) return '';
                    try {
                      const date = new Date(ts);
                      return date.toLocaleDateString('de-DE', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      });
                    } catch {
                      return '';
                    }
                  };
                  
                  return (
                    <div key={index} className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg border border-slate-100">
                      <div className={`mt-1 h-2.5 w-2.5 rounded-full flex-shrink-0 ${getActionColor(activity.action)}`}></div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-slate-700">{activity.message}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-xs text-slate-400">
                            {formatTimestamp(activity.timestamp)}
                          </span>
                          {activity.action && (
                            <Badge variant="outline" className="text-[10px] h-4 px-1.5">
                              {getActionLabel(activity.action)}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-slate-500 text-center py-8">
                Keine Aktivitäten vorhanden
              </p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
